import { Constants } from "../../Application/Constants";
import { AbstractDataSource } from "../AbstractDataSource";
import { ITokenSource } from "../ITokenSource";
import INetDataSource from "./INetDataSource";

/**
 * Represents a datasource connecting to the .Net web api controllers
 */
export class NetDataSource extends AbstractDataSource implements INetDataSource {
    private readonly scopes: string[];
    private readonly baseUrl: string;

    constructor(tokenSource: ITokenSource) {
        super(tokenSource);
        this.scopes = Constants.portal.scopes;
        this.baseUrl = Constants.portal.baseUrl;
    }

    // helpers

    protected async handleResponse(response: Response, parseResponse: boolean): Promise<any> {
        if (response.ok) {
            if (parseResponse) {
                return await response.json();
            }
        } else {
            let bodyError = new Error(`unexpected response: ${response.status}/${response.statusText}`);

            // reading as json reads the stream and its lost
            const responseClone = response.clone();

            try {
                // const json = await response.json();
                // bodyError = new Error(json ?? `${response.status}/${response.statusText}`);
                const text = await responseClone.text();
                bodyError = new Error(text ?? `${response.status}/${response.statusText}`);
            } catch {

            }

            throw bodyError;
        }
    }

    async execute(method: 'GET' | 'POST' | 'PATCH' | 'PUT', route: string, body?: any, query?: Record<string, string>): Promise<any> {
        const url = new URL(`${this.baseUrl}/${route}`);

        let token = await this.getToken(this.scopes);
        const init: RequestInit = {
            method: method,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json; charset=utf-8'
            },
        };

        if (query)
            Object.keys(query)
                .forEach(key => url.searchParams.append(key, query[key]));

        if (body)
            init.body = JSON.stringify(body);

        let response = await fetch(url.toString(), init);

        return this.handleResponse(response, true);
    }

    async executeDurable(method: string, url: string, body?: any, headers?: any, progressCallback?: (response: Response) => void): Promise<any> {


        function extractDurableHeaders(response: Response) {

            let durableInstanceId: string | null = null;
            let durableRetryAfterMS: number | null = 5000;

            for (const pair of response.headers.entries()) {
                if (pair[0] === "x-durable-instance-id")
                    durableInstanceId = pair[1];

                if (pair[0] === "x-durable-retry-after-ms")
                    durableRetryAfterMS = Number(pair[1]);
            }

            return {
                durableInstanceId,
                durableRetryAfterMS
            };
        }

        let token = await this.getToken(this.scopes);

        const requestHeaders: HeadersInit = {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json; charset=utf-8',
        }

        Object.assign(requestHeaders, headers);

        let response = await fetch(`${this.baseUrl}/${url}`, {
            method: method,
            headers: requestHeaders,
            body: JSON.stringify(body)
        });

        progressCallback?.(response);

        let { durableInstanceId, durableRetryAfterMS } = extractDurableHeaders(response);

        // ok, partial response
        while (response.ok && response.status === 202 && durableInstanceId) {

            await this.delay(durableRetryAfterMS);

            // lets wait until the time is right
            if (durableInstanceId)
                Object.assign(requestHeaders, { 'x-durable-instance-id': durableInstanceId });

            if (durableRetryAfterMS)
                Object.assign(requestHeaders, { 'x-durable-retry-after-ms': durableRetryAfterMS });

            response = await fetch(`${this.baseUrl}/${url}`, {
                method: method,
                headers: requestHeaders,
                body: JSON.stringify(body)
            });

            let t = extractDurableHeaders(response);

            if (t.durableInstanceId)
                durableInstanceId = t.durableInstanceId;

            if (t.durableRetryAfterMS)
                durableRetryAfterMS = t.durableRetryAfterMS;

            progressCallback?.(response);

        }

        if (response.ok) {
            return await response.json();
        } else {
            let body = await response.json().catch((err) => {
                response.text().then((text) => {
                    return { error: { code: response.status, message: text } };
                }).catch((err) => {
                    return { error: { code: response.status, message: 'Failed to interpret response' } };
                });
            });
            throw body;
        }
    }


    async raw(method: string, url: string, body?: any): Promise<Response> {
        let token = await this.getToken(this.scopes);
        let response = await fetch(`${this.baseUrl}/${url}`, {
            method: method,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json; charset=utf-8'
            },
            body: JSON.stringify(body)
        });
        await this.handleResponse(response, false);
        return response;
    }


    async delay(timeout: number) {
        return new Promise(resolve => {
            setTimeout(() => { resolve('') }, timeout);
        })
    }
}